import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Line } from 'react-chartjs-2';
import type { ChartOptions, TooltipItem } from 'chart.js';

import { fetchDailyIncome } from '../../services/analytics';
import { ensureChartsRegistered } from './chartSetup';

ensureChartsRegistered();

const COLORS = ['#2563EB', '#9333EA', '#F97316', '#16A34A', '#DC2626'];

interface DailyIncomeChartProps {
  restaurantId?: string;
  restaurantLabel?: string | null;
}

export default function DailyIncomeChart({
  restaurantId,
  restaurantLabel,
}: DailyIncomeChartProps = {}) {
  const [range, setRange] = useState(14);

  const { data = [], error, isError, isLoading, isFetching } = useQuery({
    queryKey: ['analytics', 'daily-income', range, restaurantId ?? 'all'],
    queryFn: () => fetchDailyIncome(range, restaurantId),
  });

  const scopeLabel = restaurantLabel ?? 'Tüm şubeler';

  const chartData = useMemo(() => {
    if (!data || data.length === 0) {
      return null;
    }

    const dates = Array.from(new Set(data.map((point) => point.date))).sort();
    const restaurantNames = Array.from(
      new Set(data.map((point) => point.restaurantName ?? 'Unknown Restaurant'))
    );

    const datasets = restaurantNames.map((name, index) => {
      const color = COLORS[index % COLORS.length];
      const values = dates.map((date) => {
        const match = data.find((point) => point.date === date && (point.restaurantName ?? 'Unknown Restaurant') === name);
        return match ? match.totalRevenue : 0;
      });
      return {
        label: name,
        data: values,
        borderColor: color,
        backgroundColor: color + '33',
        tension: 0.35,
        fill: 'origin' as const,
      };
    });

    return {
      labels: dates.map((date) =>
        new Date(date).toLocaleDateString('tr-TR', {
          day: '2-digit',
          month: 'short',
          timeZone: 'Europe/Istanbul',
        })
      ),
      datasets,
    };
  }, [data]);

  const options: ChartOptions<'line'> = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top' as const,
      },
      tooltip: {
        callbacks: {
          label: (ctx: TooltipItem<'line'>) => `₺${(ctx.parsed.y ?? 0).toFixed(2)}`,
        },
      },
    },
    scales: {
      y: {
        ticks: {
          callback: (value) => {
            const numericValue = typeof value === 'number' ? value : Number(value);
            return `₺${Number.isFinite(numericValue) ? numericValue.toFixed(0) : '0'}`;
          },
        },
      },
    },
  };

  return (
    <div className="card">
      <div className="card-header">
        <div>
          <p className="card-title">{restaurantLabel ? 'Günlük gelir trendi' : 'Restoran bazında günlük gelir'}</p>
          <p className="card-subtitle">
            {isLoading || isFetching ? 'Gelir grafiği yükleniyor...' : `Son ${range} gün · ${scopeLabel}`}
          </p>
        </div>
        <select value={range} onChange={(event) => setRange(Number(event.target.value))}>
          <option value={7}>7 gün</option>
          <option value={14}>14 gün</option>
          <option value={30}>30 gün</option>
        </select>
      </div>

      {isError ? (
        <div className="empty-state">
          <strong>Gelir verileri yüklenemedi</strong>
          <p>{error instanceof Error ? error.message : 'Orders tablosu ve restoran ilişkisinin doğruluğunu kontrol edin.'}</p>
        </div>
      ) : !chartData ? (
        <div className="empty-state">
          <strong>Henüz gelir verisi yok</strong>
          <p>Grafiği görüntülemek için en az bir teslim edilmiş sipariş olmalıdır.</p>
        </div>
      ) : (
        <Line data={chartData} options={options} />
      )}
    </div>
  );
}
